/**
 * EditableScenarioRow Component
 * 
 * A table row for scenarios with inline editing.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Loader2, Info, MapPin } from 'lucide-react';
import api from '../services/api';

function EditableScenarioRow({ scenario, onUpdate, onDelete, rowIndex = 0, isDevMode = false }) {
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Track which field is being edited
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');
    const inputRef = useRef(null);

    // Focus input when editing starts
    useEffect(() => {
        if (editingField && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingField]);

    // Start editing a field
    const startEditing = (field, currentValue) => {
        if (!isDevMode) return;
        setEditingField(field);
        setEditValue(currentValue?.toString() || '');
    };

    // Save field change
    const saveField = async (field) => {
        if (editValue === scenario[field]?.toString()) {
            setEditingField(null);
            return;
        }

        setSaving(true);
        try {
            let value = editValue;
            if (field === 'weirdnessRank') {
                value = Math.min(10, Math.max(1, parseInt(editValue) || 1));
            }

            const updated = await api.updateScenario(scenario.id, { [field]: value });
            onUpdate(updated);
            setEditingField(null);
        } catch (error) {
            console.error('Failed to save:', error);
        } finally {
            setSaving(false);
        }
    };

    // Handle key press in input
    const handleKeyDown = (e, field) => {
        if (e.key === 'Enter') {
            saveField(field);
        } else if (e.key === 'Escape') {
            setEditingField(null);
        }
    };

    // Delete scenario
    const handleDelete = async () => {
        setDeleting(true);
        try {
            await api.deleteScenario(scenario.id);
            onDelete(scenario.id);
        } catch (error) {
            console.error('Failed to delete:', error);
            alert('Failed to delete: ' + error.message);
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const isEvenRow = rowIndex % 2 === 0;

    return (
        <div className={`grid grid-cols-[minmax(300px,1fr)_150px_100px_80px] gap-4 py-3 px-4 items-center transition-colors group ${isEvenRow ? 'bg-dark-900/15' : 'bg-dark-900/45'
            } hover:bg-dark-800/60`}>
            {/* Description + Kinks */}
            <div className="min-w-0">
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {scenario.kinkNames && JSON.parse(typeof scenario.kinkNames === 'string' ? scenario.kinkNames : JSON.stringify(scenario.kinkNames)).map((name, idx) => (
                        <span key={idx} className="text-[10px] px-2 py-0.5 rounded bg-wine-900/30 text-wine-400 border border-wine-800/30 font-bold uppercase tracking-tighter">
                            {name}
                        </span>
                    ))}
                </div>
                <p className="text-cream/80 text-sm leading-relaxed line-clamp-3 italic">
                    "{scenario.scenarioDescription}"
                </p>
            </div>

            {/* Setting Type */}
            <div className="flex items-center gap-2 min-w-0">
                <MapPin className="w-3.5 h-3.5 text-wine-500/50 flex-shrink-0" />
                {editingField === 'settingType' ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveField('settingType')}
                        onKeyDown={(e) => handleKeyDown(e, 'settingType')}
                        className="w-full px-2 py-0.5 bg-dark-700 border border-wine-500/50 rounded text-xs text-dark-100 focus:outline-none"
                    />
                ) : (
                    <button
                        onClick={() => startEditing('settingType', scenario.settingType)}
                        className="text-dark-400 text-xs truncate hover:text-wine-400"
                    >
                        {scenario.settingType || 'Unknown'}
                    </button>
                )}
            </div>

            {/* Weirdness Rank */}
            <div className="flex justify-center">
                {editingField === 'weirdnessRank' ? (
                    <input
                        ref={inputRef}
                        type="number"
                        min="1"
                        max="10"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => saveField('weirdnessRank')}
                        onKeyDown={(e) => handleKeyDown(e, 'weirdnessRank')}
                        className="w-12 px-1 py-0.5 bg-dark-700 border border-wine-500/50 rounded text-center text-xs text-dark-100 focus:outline-none"
                    />
                ) : (
                    <button
                        onClick={() => startEditing('weirdnessRank', scenario.weirdnessRank)}
                        className="px-2.5 py-1 rounded-md bg-wine-900/20 text-wine-400 text-xs font-mono font-bold border border-wine-800/20"
                    >
                        {scenario.weirdnessRank}/10
                    </button>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {isDevMode && (
                    showDeleteConfirm ? (
                        <div className="flex gap-1">
                            <button onClick={handleDelete} disabled={deleting} className="w-6 h-6 bg-red-600/30 text-red-400 rounded text-xs hover:bg-red-600/40 flex items-center justify-center">
                                {deleting ? '…' : '✓'}
                            </button>
                            <button onClick={() => setShowDeleteConfirm(false)} className="w-6 h-6 text-dark-400 text-xs flex items-center justify-center hover:text-dark-200">
                                ✕
                            </button>
                        </div>
                    ) : (
                        <button onClick={() => setShowDeleteConfirm(true)} className="p-1.5 text-dark-600 hover:text-red-400 transition-colors" title="Delete">
                            <span className="text-lg">🗑</span>
                        </button>
                    )
                )}
            </div>
        </div>
    );
}

export default EditableScenarioRow;
